import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { msalConfig } from './config/msalConfig';
import { ThemeProvider } from './design/ThemeProvider';
import { ToastProvider } from './components/shared/Overlay';
import { Spinner } from './components/shared/States';
import { AppShell, BareShell, RequireAuth, RequirePermission } from './layout';
import { PAGE_PERMISSIONS, homePath } from './layout/navConfig';
import { SignInPage } from './pages/login';
import { OverviewPage } from './pages/overview';
import { WallboardPage } from './pages/wallboard';
import { AgentsPage, AgentDetailPage } from './pages/agents';
import { QueuesPage, QueueDetailPage } from './pages/queues';
import { SlaPage } from './pages/sla';
import { HeatmapPage } from './pages/heatmap';
import { CallFlowPage } from './pages/call-flow';
import { CallsPage, CallDetailPage } from './pages/calls';
import { InsightsPage } from './pages/insights';
import { ReportsPage, ReportBuilderPage } from './pages/reports';
import {
  AdminHierarchyPage,
  AdminCommunitiesPage,
  AdminDirectoryPage,
  AdminTaxonomyPage,
  AdminAiPage,
  AdminUsersPage,
  AdminSlaPage,
} from './pages/admin';
import { OnboardingPage } from './pages/onboarding';
import { HelpCenterPage } from './pages/help';

const msalInstance = new PublicClientApplication(msalConfig);

// Pages without the filter bar (mockup meta "filters": false).
const NO_FILTER_BAR = ['/calls/:id', '/admin/*', '/help', '/help/:articleId'];

function AuthLoading() {
  return (
    <div className="auth-loading">
      <Spinner label="Loading Vertolytics…" />
    </div>
  );
}

function AppContent() {
  const { isAuthenticated, loading, user, hasPermission } = useAuth();
  const location = useLocation();

  if (loading && location.pathname !== '/login') return <AuthLoading />;

  const home = user?.needsOnboarding ? '/onboarding' : homePath(hasPermission);
  const guard = (permission, element) => <RequirePermission permission={permission}>{element}</RequirePermission>;

  return (
    <Routes>
      <Route path="/login" element={<SignInPage />} />

      <Route
        element={
          <RequireAuth>
            <BareShell />
          </RequireAuth>
        }
      >
        <Route path="/wallboard" element={guard(PAGE_PERMISSIONS.wallboard, <WallboardPage />)} />
        <Route path="/onboarding" element={<OnboardingPage />} />
      </Route>

      <Route
        element={
          <RequireAuth>
            <AppShell hideFiltersOn={NO_FILTER_BAR} />
          </RequireAuth>
        }
      >
        <Route path="/overview" element={guard(PAGE_PERMISSIONS.overview, <OverviewPage />)} />
        <Route path="/agents" element={guard(PAGE_PERMISSIONS.agents, <AgentsPage />)} />
        <Route path="/agents/:id" element={guard(PAGE_PERMISSIONS.agents, <AgentDetailPage />)} />
        <Route path="/queues" element={guard(PAGE_PERMISSIONS.queues, <QueuesPage />)} />
        <Route path="/queues/:id" element={guard(PAGE_PERMISSIONS.queues, <QueueDetailPage />)} />
        <Route path="/sla" element={guard(PAGE_PERMISSIONS.sla, <SlaPage />)} />
        <Route path="/heatmap" element={guard(PAGE_PERMISSIONS.heatmap, <HeatmapPage />)} />
        <Route path="/call-flow" element={guard(PAGE_PERMISSIONS.callFlow, <CallFlowPage />)} />
        <Route path="/calls" element={guard(PAGE_PERMISSIONS.calls, <CallsPage />)} />
        <Route path="/calls/:id" element={guard(PAGE_PERMISSIONS.calls, <CallDetailPage />)} />
        <Route path="/insights" element={guard(PAGE_PERMISSIONS.insights, <InsightsPage />)} />
        <Route path="/reports" element={guard(PAGE_PERMISSIONS.reports, <ReportsPage />)} />
        <Route path="/reports/builder" element={guard(PAGE_PERMISSIONS.reports, <ReportBuilderPage />)} />
        <Route path="/admin/hierarchy" element={guard(PAGE_PERMISSIONS.settings, <AdminHierarchyPage />)} />
        <Route path="/admin/communities" element={guard(PAGE_PERMISSIONS.settings, <AdminCommunitiesPage />)} />
        <Route path="/admin/directory" element={guard(PAGE_PERMISSIONS.directory, <AdminDirectoryPage />)} />
        <Route path="/admin/taxonomy" element={guard(PAGE_PERMISSIONS.settings, <AdminTaxonomyPage />)} />
        <Route path="/admin/ai" element={guard(PAGE_PERMISSIONS.settings, <AdminAiPage />)} />
        <Route path="/admin/users" element={guard(PAGE_PERMISSIONS.users, <AdminUsersPage />)} />
        <Route path="/admin/sla" element={guard(PAGE_PERMISSIONS.settings, <AdminSlaPage />)} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/help/:articleId" element={<HelpCenterPage />} />
      </Route>

      <Route path="/dashboard" element={<Navigate to={home} replace />} />
      <Route path="*" element={<Navigate to={isAuthenticated ? home : '/login'} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <Router>
        <AuthProvider>
          <ThemeProvider>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </MsalProvider>
  );
}

export default App;
